import { useEffect, useRef, type KeyboardEvent } from 'react';

/** one row of a wheel in px, as .wheel-item in components.css (--wheel-row: 2.25rem) */
const ROW = 36;
/** a scroll that stood still this long has come to rest (Safari before 26 has no scrollend) */
const SETTLE_MS = 120;
/** a wheel holds its rows three times and rests in the middle copy, so it turns on from 59 to 00 like the phone's */
const COPIES = 3;

const two = (n: number) => String(n).padStart(2, '0');

/** "07:30" → [7, 30]; anything else → [0, 0] */
export function wheelParts(value: string): [number, number] {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value);
  const h = m ? Number(m[1]) : 0;
  const min = m ? Number(m[2]) : 0;
  return h < 24 && min < 60 ? [h, min] : [0, 0];
}

/**
 * A time as two wheels, hours and minutes, there at once (Zeitplan, Dominik 22.10.2026: a time field that opened its
 * picker only on a second tap was hard to follow). A swipe turns a wheel and it snaps to a row, a tap on a row turns to
 * it; past the last row it goes on with the first. Each wheel is a spinbutton for the keyboard and screen readers (arrows, Page up/down, Home, End). The value is
 * "HH:MM"; onChange gets the new one once the wheel has come to rest.
 */
export function TimeWheel({
  value,
  onChange,
  label,
  hourLabel,
  minuteLabel,
  rows = 5,
  disabled = false,
  testId,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  hourLabel: string;
  minuteLabel: string;
  /** rows in view: 5, or 3 for a second, smaller time in the same sheet */
  rows?: 3 | 5;
  disabled?: boolean;
  testId?: string;
}) {
  const [h, m] = wheelParts(value);
  return (
    <div
      className={['wheel', `is-rows-${rows}`, disabled ? 'is-disabled' : ''].filter(Boolean).join(' ')}
      role="group"
      aria-label={label}
      data-testid={testId}
    >
      <WheelColumn
        count={24}
        value={h}
        pageStep={6}
        label={hourLabel}
        disabled={disabled}
        onPick={(x) => onChange(`${two(x)}:${two(m)}`)}
        testId={testId && `${testId}-h`}
      />
      <span className="wheel-sep num" aria-hidden="true">
        :
      </span>
      <WheelColumn
        count={60}
        value={m}
        pageStep={10}
        label={minuteLabel}
        disabled={disabled}
        onPick={(x) => onChange(`${two(h)}:${two(x)}`)}
        testId={testId && `${testId}-m`}
      />
    </div>
  );
}

function WheelColumn({
  count,
  value,
  pageStep,
  label,
  disabled,
  onPick,
  testId,
}: {
  count: number;
  value: number;
  pageStep: number;
  label: string;
  disabled: boolean;
  onPick: (value: number) => void;
  testId: string | undefined;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);
  const onPickRef = useRef(onPick);
  /** placed at the value once it has a size (in a sheet the dialog opens after this mounts) */
  const placed = useRef(false);
  /** a turn the code started (tap, key, a new value): the rest it comes to is not the finger's */
  const steering = useRef(false);
  const settle = useRef(0);
  useEffect(() => {
    onPickRef.current = onPick;
  }, [onPick]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (placed.current || !el.clientHeight) return;
      placed.current = true;
      el.scrollTop = (count + valueRef.current) * ROW;
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      window.clearTimeout(settle.current);
    };
  }, [count]);

  // the wheel follows its value: a tap, a key or a change from outside turns it to the nearest row with it
  useEffect(() => {
    valueRef.current = value;
    const el = ref.current;
    if (!el || !placed.current) return;
    const rows = Array.from({ length: COPIES }, (_, k) => (k * count + value) * ROW);
    const top = rows.reduce((a, b) => (Math.abs(b - el.scrollTop) < Math.abs(a - el.scrollTop) ? b : a));
    if (Math.abs(el.scrollTop - top) < 1) return;
    steering.current = true;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ top, behavior: still ? 'auto' : 'smooth' });
  }, [value, count]);

  const onScroll = () => {
    window.clearTimeout(settle.current);
    settle.current = window.setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const row = Math.round(el.scrollTop / ROW);
      const at = ((row % count) + count) % count;
      // back into the middle copy, the same row in sight
      if (row < count || row >= 2 * count) el.scrollTop = (count + at) * ROW;
      if (steering.current) {
        steering.current = false;
        return;
      }
      if (at !== valueRef.current) onPickRef.current(at);
    }, SETTLE_MS);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const to: Record<string, number> = {
      ArrowUp: value - 1,
      ArrowDown: value + 1,
      PageUp: value - pageStep,
      PageDown: value + pageStep,
      Home: 0,
      End: count - 1,
    };
    const next = to[e.key];
    if (next === undefined) return;
    e.preventDefault();
    const wrapped = ((next % count) + count) % count;
    if (wrapped !== value) onPick(wrapped);
  };

  return (
    <div
      ref={ref}
      className="wheel-col"
      role="spinbutton"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={count - 1}
      aria-valuetext={two(value)}
      aria-disabled={disabled || undefined}
      onScroll={onScroll}
      onKeyDown={onKeyDown}
      data-testid={testId}
    >
      <div className="wheel-list" aria-hidden="true">
        {Array.from({ length: count * COPIES }, (_, i) => {
          const v = i % count;
          return (
            <button
              key={i}
              type="button"
              tabIndex={-1}
              className={v === value ? 'wheel-item num is-on' : 'wheel-item num'}
              disabled={disabled}
              onClick={() => v !== value && onPick(v)}
              // the test id on the middle copy only, where the wheel rests
              data-testid={testId && i >= count && i < 2 * count ? `${testId}-${two(v)}` : undefined}
            >
              {two(v)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
